// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { publishStandingLines } from 'lib/community/model'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './OwnerNoteBand.module.css'
import ClaimCta from './ClaimCta'
import { ownerLinkMessage } from './OwnerLinkMessage'
import { AcceptActions, AskOwners, askOwnersHref, MakeItCount } from './AcceptResultExtras'
import { communityThreadHref } from 'lib/community/writer-handoff'
import CoachingLines from './CoachingLines'
import OwnNoteCard from './OwnNoteCard'
import PrePublishSignIn from './PrePublishSignIn'
import SignInGatePanel from './SignInGatePanel'
import ReviewPhotosBlock from './ReviewPhotosBlock'
import ReviewPreview from 'components/community/ReviewPreview'
import PostClaimInvite from 'components/community/PostClaimInvite'
import { useReviewPhotos } from './useReviewPhotos'
import { useOwnerAuth } from 'lib/owner-notes/auth-bridge'
import { loadQuestionSet, peekQuestionSet } from 'lib/owner-notes/questions'
import {
  OWNER_HELD_MODERATOR_MESSAGE,
  OWNER_HELD_REVISIONS_CODE,
  OWNER_NOTE_MAX_CHARS,
  OWNER_NOTE_MIN_CHARS,
  OWNER_READ_VALUES,
  type OwnerAnswers,
  type OwnerClaimResponse,
  type OwnerMeResponse,
  type OwnerNoteOwn,
  type OwnerPublishResponse,
  type OwnerQuestionSetDef,
  type OwnerReadValue,
} from 'lib/dealhunt/types'
import { ownerApi, OwnerApiError, type PublishInput } from 'lib/owner-notes/api'
import {
  noteImages,
  photoErrorLine,
  expiredPhotoKeys,
  expiredPhotosLine,
  photoSendPlan,
  photosUploading,
  PHOTOS_COPY,
  sendableMediaIds,
} from 'lib/owner-notes/review-photos'
import { ensureDevice, mintDevice } from 'lib/owner-notes/device'
import { markJustPublished, useKeepSurface } from 'lib/owner-notes/session'
import { SignInGateContext } from 'lib/owner-notes/signin-gate'
import {
  attemptLink,
  GATE_LINK_FAILED,
  GATE_SIGN_IN_EXPIRED,
  gatedLinkAction,
  identityRow,
  LINK_UNAVAILABLE_LEAD,
  linkedAcceptCopy,
  maskEmail,
  type LinkOutcome,
} from 'lib/owner-notes/link'
import { useTurnstile, TurnstileError } from 'lib/owner-notes/turnstile'
import { publicConfig } from 'config'
import {
  acceptPanel,
  askOwnersSeed,
  BAND_INTRO,
  buyAgainAck,
  BUY_AGAIN_CHANGE,
  claimPublishedThisPage,
  GATE_LOGIN_DIALOG,
  handleChangeLine,
  heldClaimOffered,
  HELD_CLAIM_LEAD,
  heldEmailLine,
  heldResultLead,
  LONG_CHECK_ESCAPE_LINE,
  makeItCountItems,
  ownersDeepLink,
  prePublishSignInShown,
  resultEmailLine,
  RESULT_TIMING_LINE,
  SEE_ON_PAGE,
  shelfItemMatches,
  SIGN_IN_GATE,
  STEP3_FINE,
} from 'lib/owner-notes/band-copy'
import { OWN_PRIVATE_LABEL } from 'lib/owner-notes/hints'
import { READ_LABEL } from 'lib/owner-notes/card-owners'
import { type OwnerTrack, type OwnerTrackProduct } from 'lib/owner-notes/analytics'
import type { OwnerBandOrigin } from 'lib/owner-notes/bus'
import type { OwnerNoteDraft, OwnerNoteSeed } from 'lib/community/review'

export type OwnerBandLocation = OwnerBandOrigin | 'guide'

/** What the publish did with the photos, for a host that shows its own result (the reward
 *  screen): `attached` = the review carries ≥ 1 photo now; `dropped` = the reader's photo
 *  changes did not go (no signed profile at send time). */
export interface PublishPhotos {
  attached: boolean
  dropped: boolean
}

interface OwnerNoteBandProps {
  /** `href` = the product page, when the band sits elsewhere (category): the result links to it. */
  product: OwnerTrackProduct & { href?: string | null }
  /** The author's existing note → edit mode: PATCH instead of POST. */
  existing?: OwnerNoteOwn | null
  /**
   * What `band_opened` reports as `location`: the ask that opened it, so the open sits under
   * that ask's impressions — PDP `rail` / `section`, category `tile` (the seated invite);
   * `own_note` = an edit from the reader's own card (not an ask converting); `guide` = the
   * best-list article's one band, hosted under the reference strip. (`band` was the category
   * value before 2026-09-18.)
   */
  location: OwnerBandLocation
  trackOwner: OwnerTrack
  onClose: () => void
  /** The server answered — the parent renders the own-note card optimistically from it. */
  onResult: (result: OwnerPublishResponse, photos?: PublishPhotos) => void
  /** A claim from inside the band linked the identity (and may have published the note). */
  onClaimed?: (result: OwnerClaimResponse) => void
  /**
   * This browser's `/me` (null while unknown) — drives the identity row (#81), including the
   * "linking also publishes your held reviews" disclosure. Required: My gear once left it out
   * and published held notes on link with no line saying so (audit 2026-09-13, capture-4).
   */
  me: OwnerMeResponse | null
  /** The reader chose "start fresh on this browser" (#81 conflict): the device is gone. */
  onDeviceForgotten?: () => void
  /** The ask that opened the band WAS the buy-again question (the guide's reference strip,
   *  2026-09-17): its answer arrives already given, and step 2 shows it chosen — still theirs
   *  to change. Ignored in edit mode: a note's stored answer is never overwritten by a tap. */
  seedBuyAgain?: boolean
  /**
   * The answers and sentence as they stand, on every change (and once on open) — for a host
   * that previews the review beside the form (the community form, `pages/community/review/new`).
   * Read-only: nothing the host does with it reaches the band.
   */
  onDraftChange?: (draft: OwnerNoteDraft) => void
  /**
   * A draft to start from — the community form restoring what the reader typed before a reload
   * (`sessionStorage`, `lib/community/writer-handoff`). Read once, on the first render, like
   * `seedBuyAgain` (which wins over the seed's own buy-again: the tap is the fresher answer).
   * Ignored in edit mode: a stored note is never overwritten by a draft. This is the one way a
   * host's draft reaches the band; `onDraftChange` stays read-only.
   */
  seed?: OwnerNoteSeed | null
  /** Where this tab keeps a new review's uploaded photos across a reload (the community form:
   *  `reviewPhotosKey`); none elsewhere. Never read in edit mode. */
  photoDraftKey?: string | null
  /** The host prints the band's title itself (the community form's h1): the heading stays for
   *  focus and screen readers, visually hidden. */
  hideTitle?: boolean
  /**
   * The host prints its own preview and sign-in panel beside the band (the community form:
   * `ReviewPreview` + `ReviewPublishPanel`): the band leaves out its step-3 preview and its
   * pre-publish sign-in line, so the page never shows two of either.
   */
  hostPanels?: boolean
  /**
   * Where this tab keeps a gated create's draft (`sessionStorage`, the PDP Owners section:
   * product id + surface), so "What you write stays here" holds across the email sign-in detour
   * and a tab discard. Read once on open and written on every change — only under the sign-in
   * gate (`SignInGateContext`) and never in edit mode; cleared on a server answer and on close.
   * Photos are not in it (they keep their own `photoDraftKey`). A host that keeps its own draft
   * (the community form's `seed`) passes none.
   */
  draftKey?: string | null
}

type Step = 1 | 2 | 3

/**
 * What Publish is doing right now. One "Checking…" for the whole pipeline read as stale:
 * Cloudflare's widget turned to "Success!" while the button stayed the same for the note
 * check that follows. Each step names itself instead. (Photos upload at pick time, before
 * Publish — no photo phase.)
 */
type PublishPhase = 'bot' | 'link' | 'check'
const PUBLISH_PHASE_LABEL: Record<PublishPhase, string> = {
  bot: 'Confirming you’re human…',
  link: 'Linking to your account…',
  check: 'Checking your review…',
}
/** The note check is the long step (the automatic read): say it's still going, no promise
 *  of how long (copy constraint — no durations). */
const STILL_CHECKING_LABEL = 'Still checking your review…'
const STILL_CHECKING_AFTER_MS = 6000

/** Under a rejection: what was judged, and what the reader keeps. */
const REJECT_SCOPE_LINE =
  'The automatic check reads your sentence against our owner-review rules; your answers are kept as they are.'

const emptyAnswers = (): OwnerAnswers => ({
  ownership_length: '',
  still_using: '',
  use_cases: [],
  reads: {},
  gave_out: null,
  hours_per_charge: null,
  buy_again: undefined as unknown as boolean,
})

/** What `draftKey` holds: the form as the reader left it (photos excluded). */
interface StoredBandDraft {
  answers: OwnerAnswers
  note: string
  step: Step
  buyAgainReopened: boolean
  /** This band already showed the gate (claim analytics read `gate_publish` after a discard). */
  gateShown: boolean
}

/** `sessionStorage`, never throwing: a private window or blocked storage just loses the draft. */
function readBandDraft(key: string | null): StoredBandDraft | null {
  if (!key || typeof window === 'undefined') {
    return null
  }
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) {
      return null
    }
    const d = JSON.parse(raw) as Partial<StoredBandDraft> | null
    if (
      !d ||
      typeof d !== 'object' ||
      !d.answers ||
      typeof d.answers !== 'object' ||
      typeof d.note !== 'string' ||
      (d.step !== 1 && d.step !== 2 && d.step !== 3)
    ) {
      return null
    }
    return {
      answers: { ...emptyAnswers(), ...d.answers },
      note: d.note,
      step: d.step,
      buyAgainReopened: d.buyAgainReopened === true,
      gateShown: d.gateShown === true,
    }
  } catch {
    return null
  }
}
function writeBandDraft(key: string, draft: StoredBandDraft | null): void {
  try {
    if (draft) {
      window.sessionStorage.setItem(key, JSON.stringify(draft))
    } else {
      window.sessionStorage.removeItem(key)
    }
  } catch {
    // Storage full or blocked: the draft is a convenience.
  }
}

/** Email sign-in is offered in the dialog: what the bridge knows, else `ensureEmailSignIn`'s
 *  answer. Only the gate's email-link hint reads it; false (no hint) while still asking. */
function emailOfferedFor(known: boolean | null | undefined, answer: boolean | null): boolean {
  if (typeof known === 'boolean') {
    return known
  }
  return answer === true
}

const SUBMIT_LABEL = { edit: 'Resend', create: 'Publish' } as const

/**
 * The 30-second owner note (contract §1, #65): three steps — the ownership answers, the
 * four reads (+ hours, buy again), the sentence + optional public photos — then publish. No handle
 * step: the pseudonym comes back from the device mint. The result renders in place:
 * accept → "you're owner #N"; review → held for a moderator with the claim CTA (#64);
 * reject → the judge's `reader_message` with "Edit and resend" and an appeal (#57);
 * hold → checked again automatically (#20).
 *
 * Signed-in readers (#81): the identity row above Publish says the note will be linked to
 * their account; the claim runs right before the note is sent so it publishes instantly.
 * The Publish click is the consent moment — nothing links on band open. See
 * `docs/owner-notes-signed-in-publish.md`.
 */
const OwnerNoteBand: React.VFC<OwnerNoteBandProps> = ({
  product,
  existing = null,
  location,
  trackOwner,
  onClose,
  onResult,
  onClaimed,
  me,
  onDeviceForgotten,
  seedBuyAgain,
  onDraftChange,
  seed = null,
  photoDraftKey = null,
  hideTitle = false,
  hostPanels = false,
  draftKey = null,
}) => {
  const ownerAuth = useOwnerAuth()
  // The sign-in gate (option D): a gated host wraps the band; creates only — an edit had no
  // notice at its "Edit", so gating it would be the surprise wall.
  const gatedCreate = useContext(SignInGateContext) && !existing
  const storeKey = gatedCreate ? draftKey : null
  // A gated create's draft from this tab (the email detour, a discard): read once, here, so the
  // form opens as the reader left it — never through an effect (a flash of step 1, and the
  // write below would overwrite it first).
  const [restored] = useState<StoredBandDraft | null>(() => readBandDraft(storeKey))
  // Literal 1 when nothing was restored: the step specs force the step through `useState(1)`.
  const [step, setStep] = useState<Step>(restored?.step ?? 1)
  // Preloaded on the category tile / PDP section: opens with the questions painted.
  const [questions, setQuestions] = useState<OwnerQuestionSetDef | null>(() =>
    peekQuestionSet(product.nodeId)
  )
  const [questionsError, setQuestionsError] = useState(false)
  /** The load itself failed (timeout, network, 5xx) — not "no question set": offer a retry. */
  const [questionsFailed, setQuestionsFailed] = useState(false)
  const [questionsAttempt, setQuestionsAttempt] = useState(0)
  const [answers, setAnswers] = useState<OwnerAnswers>(() => {
    if (existing) {
      return { ...emptyAnswers(), ...existing.answers }
    }
    if (restored) {
      return restored.answers
    }
    const base = seed
      ? {
          ...emptyAnswers(),
          ...seed.answers,
          buy_again: (seed.answers.buy_again ?? undefined) as unknown as boolean,
        }
      : emptyAnswers()
    return typeof seedBuyAgain === 'boolean' ? { ...base, buy_again: seedBuyAgain } : base
  })
  const [note, setNote] = useState(
    existing ? existing.note ?? '' : restored?.note ?? seed?.note ?? ''
  )
  // The ask that opened the band already took the buy-again answer (`seedBuyAgain`): step 1 says
  // so and step 2 does not ask again — unless the reader taps "change". Read once, never in edit
  // mode (the seed rule: a stored answer is never overwritten by a tap).
  const [buyAgainSeeded] = useState(() => !existing && typeof seedBuyAgain === 'boolean')
  const [buyAgainReopened, setBuyAgainReopened] = useState(restored?.buyAgainReopened ?? false)
  const buyAgainAsked = !buyAgainSeeded || buyAgainReopened
  const [busy, setBusy] = useState(false)
  const [phase, setPhase] = useState<PublishPhase | null>(null)
  const [stillChecking, setStillChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * #150 — the held note's edit ceiling. DealHunt's sentence, held apart from `error` because
   * it is INFORMATION: nothing was lost, the waiting version is intact, and no retry will
   * succeed. So it renders in the calm style and takes Publish away instead of offering
   * another tap. The other 429s (`budget:device:*`, `budget:profile:*`, `budget:ip:*`) DO
   * mean "come back in an hour" and stay on the ordinary `error` path.
   */
  const [editCapped, setEditCapped] = useState<string | null>(null)
  /** The last send went without the photo changes (no signed profile at send time). */
  const [photosDropped, setPhotosDropped] = useState(false)
  const [result, setResult] = useState<OwnerPublishResponse | null>(null)
  const [appealed, setAppealed] = useState(false)
  const [claimed, setClaimed] = useState<OwnerClaimResponse | null>(null)
  // #81: the reader's choice for this band session, the failed-link CTA, the 409 stop.
  const [linkAccount, setLinkAccount] = useState(true)
  const [linkFailure, setLinkFailure] = useState<Extract<LinkOutcome, { kind: 'failed' }> | null>(
    null
  )
  const [conflict, setConflict] = useState<string | null>(null)
  // The conflict belongs to the sign-in whose claim got the 409: a sign-out (or another
  // account) drops it, so "Start fresh" never runs for a session that is gone.
  const conflictToken = ownerAuth.credential?.token ?? null
  useEffect(() => {
    if (gatedCreate) {
      setConflict(null)
    }
  }, [gatedCreate, conflictToken])
  /** Under the gate, why the last Publish stopped before sending (fail-closed link failure). */
  const [gateStop, setGateStop] = useState<{ retry: 'link' | 'sign_in'; text: string } | null>(null)
  /** The session token whose claim came back "sign in again": the gate shows while it is still
   *  the session's — a NEW token re-renders Publish and nothing else (no claim on sign-in). */
  const [staleToken, setStaleToken] = useState<string | null>(null)
  /** `ensureEmailSignIn`'s answer, when the bridge did not know yet. */
  const [emailAnswer, setEmailAnswer] = useState<boolean | null>(null)
  /** This band showed the gate: the Publish-click claim then reports `gate_publish`. */
  const gateShownRef = useRef(restored?.gateShown ?? false)
  /** A server answer (or a close) ended this draft: nothing writes it back. */
  const draftDoneRef = useRef(false)
  /** The check's reason after a rejection, shown above the sentence while it is edited. */
  const [revisionHint, setRevisionHint] = useState<string | null>(null)
  // Public photos (contract-review-photos): uploaded at pick time, attached by `media_ids`.
  // Edit mode starts from the note's photos; `photoBaseline` = what DealHunt last got, so a
  // PATCH carries `media_ids` only when the set changed (absent keeps the photos as they are).
  const photoSet = useReviewPhotos({
    initial: existing ? noteImages(existing) : null,
    storageKey: existing ? null : photoDraftKey,
    credential: ownerAuth.credential,
    showLogIn: ownerAuth.showLogIn,
    loginOpen: ownerAuth.loginOpen,
    holdForSession: gatedCreate,
  })
  const photoBaseline = useRef<string[]>(
    existing ? noteImages(existing).map(im => im.media_id) : []
  )
  const noteFieldRef = useRef<HTMLTextAreaElement>(null)
  const bandTokenRef = useRef<string>('')
  const noteIdRef = useRef<string | null>(existing?.id ?? null)
  /** The honeypot input (§5): read at publish, never held in React state. */
  const websiteRef = useRef<HTMLInputElement>(null)
  const turnstile = useTurnstile()
  const headingRef = useRef<HTMLHeadingElement>(null)

  // Band open: the timing token (§5) + the analytics mark.
  useEffect(() => {
    trackOwner('band_opened', { location })
    void ownerApi
      .bandToken()
      .then(token => {
        bandTokenRef.current = token
      })
      .catch(() => undefined)
    // No scroll jump on open: the band opens where the reader clicked. Not when the host prints
    // the title (`hideTitle`, the community form): the band is the page there, and moving focus
    // to a hidden heading would skip what the host put before it (the phone's sign-in line).
    if (!hideTitle) {
      headingRef.current?.focus({ preventScroll: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open
  }, [product.nodeId])

  // The question set: once per open, and again on "Try again". A null answer is the
  // category having no question set (a real 404); a rejection is the load failing —
  // the client's own 10 s timeout, a network error, a 5xx — which the guide surfaced as
  // "not set up for this category" during a slow moment after a DealHunt deploy
  // (2026-09-15). The two get different sentences; only the failure offers a retry
  // (loadQuestionSet evicts a failed load, so the retry really refetches).
  useEffect(() => {
    // A cleanup (product change, StrictMode's dev double-mount) only drops a late answer:
    // the shared load keeps going — flagging a cancel painted "not set up for this
    // category" over a form whose questions had loaded fine on the second run.
    let cancelled = false
    setQuestionsError(false)
    setQuestionsFailed(false)
    setQuestions(peekQuestionSet(product.nodeId))
    loadQuestionSet(product.nodeId)
      .then(qs => {
        if (cancelled) {
          return
        }
        if (qs) {
          setQuestions(qs)
        } else {
          setQuestionsError(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQuestionsFailed(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [product.nodeId, questionsAttempt])

  // The draft, for a host that previews it. The callback rides in a ref: a host that passes an
  // inline function and sets state from it must not re-run this effect on its own re-render.
  const onDraftChangeRef = useRef(onDraftChange)
  onDraftChangeRef.current = onDraftChange
  // Photos should ride this publish: a session that links at Publish, or a device already
  // linked (contract-review-photos §2). A PREDICTION for the panel and the picker's line — the
  // send itself decides after the link step (`photoSendPlan`).
  const canAttachPhotos = Boolean(
    (ownerAuth.signedIn && ownerAuth.credential && linkAccount) || me?.profile.claimed || claimed
  )
  // Only uploaded photos count (a tile still on its way up, or failed, is not on the review).
  const photoIds = sendableMediaIds(photoSet.photos, Date.now())
  const firstPhoto = photoSet.photos.find(p => p.media_id && photoIds.includes(p.media_id))
  const cover = firstPhoto
    ? photoSet.previews[firstPhoto.key] ?? firstPhoto.preview_url ?? firstPhoto.url
    : null
  const photoKey = photoIds.join(',')
  // Every pick for the step-3 preview's strip — held before the gate's sign-in, uploading or
  // done; a failed tile is not on the review. The draft's `cover`/`photoCount` stay uploaded-only.
  const previewPhotos = photoSet.photos.flatMap(p => {
    const src = p.status === 'error' ? null : photoSet.previews[p.key] ?? p.preview_url ?? p.url
    return src ? [{ src }] : []
  })
  // One draft for the host's preview (`onDraftChange`) and the band's own step-3 preview.
  const draft = useMemo<OwnerNoteDraft>(
    () => ({
      answers: {
        ...answers,
        // `emptyAnswers` holds `undefined` under a boolean type until the reader answers.
        buy_again: typeof answers.buy_again === 'boolean' ? answers.buy_again : null,
      },
      note,
      // Only photos this publish can carry earn the panel's "Photo +15" (no signed profile → none).
      hasPhoto: canAttachPhotos && photoIds.length > 0,
      photoCount: photoIds.length,
      cover,
    }),
    // `photoKey` stands for the ids (a new array each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answers, note, photoKey, cover, canAttachPhotos]
  )
  useEffect(() => {
    onDraftChangeRef.current?.(draft)
  }, [draft])

  // "Edit and resend" lands on the sentence with the cursor in it.
  useEffect(() => {
    if (revisionHint && step === 3 && !result) {
      noteFieldRef.current?.focus({ preventScroll: true })
    }
  }, [revisionHint, step, result])

  let publishLabel: string = SUBMIT_LABEL[existing ? 'edit' : 'create']
  if (phase === 'check' && stillChecking) {
    publishLabel = STILL_CHECKING_LABEL
  } else if (phase !== null) {
    publishLabel = PUBLISH_PHASE_LABEL[phase]
  } else if (busy) {
    publishLabel = 'Checking…'
  }

  const anyWorse = useMemo(
    () => Object.values(answers.reads).some(v => v === 'worse'),
    [answers.reads]
  )
  const readsAnswered = Object.values(answers.reads).filter(
    v => v !== null && v !== undefined
  ).length
  const noteLength = note.trim().length
  // #81: a session means a credential (`useOwnerAuth` — the sign-in's check on load).
  const sessionEmail = ownerAuth.signedIn ? maskEmail(ownerAuth.email) : null
  // #121: only the held notes linking would publish — not appealed or out-of-resubmits ones.
  const heldCount = me?.shelf.filter(item => heldClaimOffered(item.note, false)).length ?? 0
  // ── the sign-in gate (docs/owner-signin-gate-plan.md §3) ──
  // The gate is a live session (2026-10-17): a device already linked to an account
  // (`me.profile.claimed`) meets it too — #114 forgets the device only on a Logout click, so a
  // lapsed session would otherwise keep publishing as the account on a shared computer.
  // Signed out, resolved: the gate panel and "Sign in to publish" take the step.
  const gateActive = gatedCreate && ownerAuth.resolved && !ownerAuth.signedIn
  // The session check has not answered: Publish waits — never an anonymous send meanwhile.
  const gatePending = gatedCreate && !ownerAuth.resolved
  // The last claim said "sign in again" and the session still carries that token.
  const gateExpired =
    gatedCreate && staleToken !== null && (ownerAuth.credential?.token ?? null) === staleToken
  const gateShows = gateActive || gateExpired
  if (gateShows && step === 3) {
    gateShownRef.current = true
  }
  const emailOffered = emailOfferedFor(ownerAuth.emailSignIn, emailAnswer)
  // Why the last gated Publish stopped (the expired line), while the gate is back on that token.
  const gateStopped = gateExpired && gateStop?.retry === 'sign_in' ? gateStop.text : null
  // The gate's lead (2026-10-17): the footer of the band's own preview card (its byline already
  // names the reader); a standalone box only when the host renders its own preview (hostPanels).
  const gatePanel = (inPreview: boolean): React.ReactElement => (
    <SignInGatePanel
      emailOffered={emailOffered}
      linkedHandle={me?.profile.claimed ? me.profile.handle : null}
      stopped={gateStopped}
      inPreview={inPreview}
    />
  )
  const identity = gateShows
    ? null
    : identityRow({
        sessionEmail,
        profile: me?.profile ?? null,
        heldCount,
        linkAccount,
        gated: gatedCreate,
        signedIn: ownerAuth.signedIn,
      })
  // Step 3's sign-in line (2026-10-06): opens the dialog only; linking stays at Publish (#81).
  // Under the gate the gate panel takes its slot.
  const showSignInFirst =
    !gatedCreate &&
    prePublishSignInShown({
      hostPanels,
      counts: existing?.status !== 'published',
      resolved: ownerAuth.resolved,
      signedIn: ownerAuth.signedIn,
      claimed: Boolean(me?.profile.claimed),
    })
  const signInFirst = (): Promise<void> => {
    trackOwner('claim_started', { location: 'pre_publish' })
    return ownerAuth.showLogIn()
  }
  const step1Ok = Boolean(answers.ownership_length) && Boolean(answers.still_using)
  const step2Ok = readsAnswered > 0 && typeof answers.buy_again === 'boolean'
  const step3Ok = noteLength >= OWNER_NOTE_MIN_CHARS && noteLength <= OWNER_NOTE_MAX_CHARS
  const uploadingPhotos = photosUploading(photoSet.photos)

  const goTo = (next: Step): void => {
    if (next > step) {
      // The gate is reached on the 2→3 step: the same one event, carrying `location: gate` —
      // no extra fire, so step totals do not inflate. Ungated payloads are unchanged.
      trackOwner(
        'step',
        gateActive && next === 3 ? { position: step, location: 'gate' } : { position: step }
      )
    }
    setStep(next)
    setError(null)
    // The ceiling belongs to one send, not to the band: a reopened form must not stay locked.
    setEditCapped(null)
  }

  const setRead = (id: string, value: OwnerReadValue): void =>
    setAnswers(a => ({
      ...a,
      reads: { ...a.reads, [id]: a.reads[id] === value ? null : value },
    }))

  const toggleUseCase = (id: string): void =>
    setAnswers(a => {
      if (a.use_cases.includes(id)) {
        return { ...a, use_cases: a.use_cases.filter(u => u !== id) }
      }
      if (a.use_cases.length >= 6) {
        return a
      }
      return { ...a, use_cases: [...a.use_cases, id] }
    })

  // The gate's email-link hint shows only once commentHub says email is offered: ask on open.
  // The step-1 line and the gate lead name no method and never wait for this (2026-10-17).
  const ensureEmailSignIn = ownerAuth.ensureEmailSignIn
  const emailKnown = typeof ownerAuth.emailSignIn === 'boolean'
  useEffect(() => {
    if (!gatedCreate || emailKnown || !ensureEmailSignIn) {
      return undefined
    }
    let cancelled = false
    void ensureEmailSignIn()
      .catch(() => false)
      .then(available => {
        if (!cancelled) {
          setEmailAnswer(available)
        }
      })
    return () => {
      cancelled = true
    }
  }, [gatedCreate, emailKnown, ensureEmailSignIn])

  // The gated create's draft, kept per tab on every change (sessionStorage is synchronous and
  // the draft small: no debounce, so no late write can resurrect a cleared draft).
  useEffect(() => {
    if (!storeKey || draftDoneRef.current) {
      return
    }
    writeBandDraft(storeKey, {
      answers,
      note,
      step,
      buyAgainReopened,
      gateShown: gateShownRef.current,
    })
  }, [storeKey, answers, note, step, buyAgainReopened, gateShows])

  /** The draft is over (a server answer, or the reader closed the band): forget it. */
  const endDraft = (): void => {
    draftDoneRef.current = true
    if (storeKey) {
      writeBandDraft(storeKey, null)
    }
  }
  /** Every way out of the band clears a gated draft — a tab discard (no close) keeps it. */
  const close = (): void => {
    endDraft()
    onClose()
  }

  // Keep-it-yours (3b903d15): while a gated step 3 is on screen, this band offers the link at
  // Publish — the app-wide prompt stays away, or a sign-in mid-band would double the offer.
  useKeepSurface(gatedCreate && step === 3)

  useEffect(() => {
    setStillChecking(false)
    if (phase !== 'check') {
      return undefined
    }
    const timer = setTimeout(() => setStillChecking(true), STILL_CHECKING_AFTER_MS)
    return () => clearTimeout(timer)
  }, [phase])

  /**
   * One `coaching_shown` for the recognition, the only slot the result renders (#144/#149) —
   * `prompt_id` is the closed frame id, which goes to GA4 and nowhere else. The band's result
   * panel is on screen the moment it renders (the reader just tapped Publish), so this needs no
   * viewport observer, unlike an ask impression. `coaching` is null for nearly every note today;
   * then nothing fires. The clue's impression fires from `OwnNoteCard`, where the clue is shown.
   */
  const recognitionPromptId = result?.verdict.coaching?.recognition?.prompt_id ?? null
  useEffect(() => {
    if (recognitionPromptId) {
      trackOwner('coaching_shown', { kind: 'recognition', prompt_id: recognitionPromptId })
    }
    // The id identifies the render; `trackOwner` is stable per product.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recognitionPromptId])

  /** A fresh Turnstile token, with the button saying so while Cloudflare decides. */
  const botToken = (): Promise<string> => {
    setPhase('bot')
    return turnstile.getToken()
  }

  const publish = async (): Promise<void> => {
    if (busy) {
      return
    }
    // The gate guards the send itself, not only the button (a stale render, a script): a gated
    // create goes out only with a live session, after the claim below — a linked device too
    // (its same-subject claim is an idempotent 200, 49cebd39). Unresolved: wait. No session (or
    // the one whose sign-in expired): this click opens the dialog — sign-in only, nothing claims
    // and nothing is sent; the reader clicks Publish again once signed in.
    if (gatedCreate) {
      if (!ownerAuth.resolved) {
        return
      }
      if (!ownerAuth.credential || gateExpired) {
        gateShownRef.current = true
        trackOwner('claim_started', { location: 'gate' })
        void ownerAuth.showLogIn({ copy: GATE_LOGIN_DIALOG })
        return
      }
    }
    if (!step3Ok || uploadingPhotos) {
      return
    }
    // A photo uploaded over 23 h ago may be gone from DealHunt: mark it, say so, send nothing
    // (9bbdd9b7 — never drop a photo unseen).
    const expired = expiredPhotoKeys(photoSet.photos, Date.now())
    if (expired.length) {
      photoSet.markExpired(expired)
      setError(expiredPhotosLine(expired.length))
      return
    }
    setBusy(true)
    // Named from the first frame — never the old catch-all "Checking…" (a claim, when due,
    // re-labels it right away).
    setPhase('bot')
    setError(null)
    setGateStop(null)
    const resubmit = noteIdRef.current !== null
    trackOwner('publish_attempt', { action: resubmit ? 'resubmit' : 'publish' })
    // Photos ride only a send that resolves to a signed profile — decided after the link step,
    // never from the render's prediction (`canAttachPhotos`).
    let signed = Boolean(me?.profile.claimed || claimed)
    let sendDropped = false
    try {
      await ensureDevice(botToken)
      // #81: link BEFORE the note so a judge-accepted note publishes instantly (#64). Under the
      // gate the session links whether or not it carries an email (keyed on the credential —
      // `sessionEmail` alone would let an email-less session through anonymously), and the
      // claim reports `gate_publish` when this band showed the gate.
      const claimDue = gatedCreate
        ? Boolean(ownerAuth.credential) && !claimed
        : Boolean(sessionEmail && ownerAuth.credential && linkAccount && !claimed)
      const claimLocation = gatedCreate && gateShownRef.current ? 'gate_publish' : 'publish'
      if (claimDue && ownerAuth.credential) {
        trackOwner('claim_started', { location: claimLocation })
        setPhase('link')
        const credential = ownerAuth.credential
        const outcome = await attemptLink(ownerApi.claim, credential)
        if (outcome.kind === 'conflict') {
          // This browser's profile belongs to another account: never publish under it.
          setConflict(outcome.message)
          trackOwner('publish_result', { action: 'already_claimed' })
          return
        }
        if (outcome.kind === 'linked') {
          signed = true
          setClaimed(outcome.result)
          setLinkFailure(null)
          onClaimed?.(outcome.result)
          trackOwner('claim_done', {
            location: claimLocation,
            action: ownerAuth.provider ?? 'other',
            item_count: outcome.result.published_now,
          })
        } else if (gatedCreate && gatedLinkAction(outcome) === 'stop') {
          // Fail-closed (gated): a failure the reader can fix sends nothing and keeps the draft.
          // `sign_in` brings the gate back until a new token arrives; `link` says try again.
          if (outcome.retry === 'sign_in') {
            setStaleToken(credential.token)
            setGateStop({ retry: 'sign_in', text: GATE_SIGN_IN_EXPIRED })
          } else {
            setGateStop({ retry: 'link', text: GATE_LINK_FAILED })
          }
          trackOwner('publish_result', { action: outcome.code })
          return
        } else {
          setLinkFailure(outcome)
        }
      }
      if (!bandTokenRef.current) {
        bandTokenRef.current = await ownerApi.bandToken().catch(() => '')
      }
      const cleanAnswers: OwnerAnswers = {
        ...answers,
        gave_out: anyWorse ? answers.gave_out ?? null : null,
      }
      const plan = photoSendPlan({
        signed,
        photos: photoSet.photos,
        baseline: photoBaseline.current,
        now: Date.now(),
      })
      sendDropped = plan.dropped
      const { mediaIds } = plan
      const baselineBefore = photoBaseline.current
      const input: Omit<PublishInput, 'node_id'> = {
        answers: cleanAnswers,
        note: note.trim(),
        turnstile_token: await botToken(),
        band_token: bandTokenRef.current,
        // The honeypot's real value — a script that fills every field trips the wall.
        website: websiteRef.current?.value ?? '',
        ...(mediaIds ? { media_ids: mediaIds } : {}),
      }
      const send = (): Promise<OwnerPublishResponse> => {
        setPhase('check')
        if (noteIdRef.current) {
          return ownerApi.resubmit(noteIdRef.current, input)
        }
        return ownerApi.publish({ node_id: product.nodeId, ...input })
      }
      /** Send again without the photos: the review never fails for them. */
      const dropPhotos = (): void => {
        if (input.media_ids) {
          delete input.media_ids
          sendDropped = true
        }
      }
      let res: OwnerPublishResponse
      try {
        res = await send()
      } catch (e) {
        if (
          e instanceof OwnerApiError &&
          (e.code === 'device_required' || e.code === 'device_unknown')
        ) {
          // A dead device cookie (DealHunt forgot the token): mint once and retry. A fresh device
          // is never claimed — the photos cannot go with this send.
          await mintDevice(botToken)
          if (gatedCreate) {
            // Gated: the fresh device is unclaimed, so this send would go out anonymously. Stop,
            // keep the draft, and let the next click pass the gate and the claim again.
            setClaimed(null)
            onDeviceForgotten?.()
            setGateStop({ retry: 'link', text: GATE_LINK_FAILED })
            trackOwner('publish_result', { action: e.code })
            return
          }
          dropPhotos()
          input.turnstile_token = await botToken()
          res = await send()
        } else if (
          e instanceof OwnerApiError &&
          e.code === 'photos_need_sign_in' &&
          input.media_ids
        ) {
          // DealHunt does not see a signed profile after all: publish without the photos.
          dropPhotos()
          input.turnstile_token = await botToken()
          res = await send()
        } else {
          throw e
        }
      }
      noteIdRef.current = res.note.id
      if (res.note.status === 'published') {
        // The accept result says "live" now: the app-wide return card waits (2026-10-09).
        markJustPublished(res.note.id)
      }
      if (input.media_ids) {
        photoBaseline.current = input.media_ids
      }
      setPhotosDropped(sendDropped)
      // What the review carries now: the set sent, or — untouched — what it had before.
      const attached = (input.media_ids ?? (sendDropped ? [] : baselineBefore)).length > 0
      // DealHunt holds the photos with the note now: the tab's stored set is done.
      photoSet.forgetStored()
      trackOwner('publish_result', { action: res.verdict.outcome })
      // The review exists on DealHunt now (any verdict): a restored draft would send a second.
      endDraft()
      setResult(res)
      onResult(res, { attached, dropped: sendDropped })
    } catch (e) {
      let code = 'error'
      if (e instanceof OwnerApiError) {
        code = e.code
      } else if (e instanceof TurnstileError) {
        code = 'bot_check_unavailable'
      }
      trackOwner('publish_result', { action: code })
      const photoLine = e instanceof OwnerApiError ? photoErrorLine(e.code) : null
      if (e instanceof OwnerApiError && e.code === OWNER_HELD_REVISIONS_CODE) {
        // Not a failure: say so in DealHunt's own words and stop offering the send.
        setEditCapped(e.message)
      } else if (photoLine) {
        // A photo refusal (§2): the review is not sent; say what to do about the photos.
        setError(photoLine)
      } else {
        setError(e instanceof Error ? e.message : 'Something went wrong — try again.')
      }
    } finally {
      setBusy(false)
      setPhase(null)
    }
  }

  // #81 conflict exit: drop this browser's device, tell the parent, publish again (the
  // mint inside `publish` starts a fresh pseudonym, which then links to the session).
  const startFresh = async (): Promise<void> => {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      await ownerApi.forgetDevice()
      setConflict(null)
      onDeviceForgotten?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong — try again.')
      return
    } finally {
      setBusy(false)
    }
    // Gated and signed out since the conflict (the header's sign-out): this closure still sees
    // the forgotten device as linked — stop here, so the next click meets the gate.
    if (gatedCreate && !ownerAuth.credential) {
      setConflict(null)
      return
    }
    await publish()
  }

  const appeal = async (): Promise<void> => {
    if (!noteIdRef.current || busy) {
      return
    }
    setBusy(true)
    try {
      await ownerApi.appeal(noteIdRef.current)
      trackOwner('appeal')
      setAppealed(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong — try again.')
    } finally {
      setBusy(false)
    }
  }

  const editAndResend = (): void => {
    // Carry the reason into the sentence step: the reader edits with it in view.
    setRevisionHint(result?.verdict.reader_message ?? null)
    // The photos went with the first attempt (`photoBaseline`): a resend carries `media_ids`
    // only if the reader changes them.
    setResult(null)
    setStep(3)
    setError(null)
  }

  // ── result states ──────────────────────────────────────────────────────────
  if (result) {
    const { outcome, reader_message: readerMessage } = result.verdict
    const shelfRow = me?.shelf.find(item => shelfItemMatches(item, product.nodeId)) ?? null
    // #120: this note went live through the claim only when DealHunt names this product's held
    // note — never from the profile-wide `published_now` (audit capture-1). null = old DealHunt.
    const liveHere = claimed
      ? claimPublishedThisPage(claimed, result.note, product.nodeId, shelfRow)
      : false
    const linkedCopy = claimed
      ? linkedAcceptCopy(product.name, result.owners_now, claimed, [
          product.nodeId,
          result.note.node_id,
          ...(shelfRow?.node_ids ?? []),
        ])
      : null
    // Linked at this publish, or the device was already the reader's account.
    const linkedEmail =
      claimed?.email_masked ?? (me?.profile.claimed ? me.profile.email_masked : null) ?? null
    const emailLine = resultEmailLine({ linkedEmail })
    // What the reader just wrote, as it now stands (2026-09-11): the result used to show
    // only a verdict — and, when held, a sign-in box — never the note itself.
    // A sign-in after the result re-homes the note: the account's name (a merge into an
    // existing profile renames it) and, for a held note, published now.
    let shownNote = claimed ? { ...result.note, handle: claimed.handle } : result.note
    if (liveHere) {
      shownNote = { ...shownNote, status: 'published', reader_message: null }
    }
    const renamedLine = claimed ? handleChangeLine(result.note.handle, claimed.handle) : null
    // #121: a `review` can be moderator-only (out of resubmits): no "passed our checks", no
    // sign-in-skips-the-moderator button — DealHunt's sentence instead.
    const signInPublishes = heldClaimOffered(
      { status: 'held_review', reader_message: readerMessage },
      false
    )
    const summary = (
      <OwnNoteCard
        note={shownNote}
        claimed={Boolean(claimed) || Boolean(me?.profile.claimed)}
        trackOwner={trackOwner}
        location="own_note"
        questions={questions}
        /* The result shows no clue at all (Guillaume, 2026-09-22): it asks for an edit, and the
           result has no Edit — a first-time author would not know to go to the product page. The
           clue waits on the author's card, next to Edit, on every other host (the PDP, the
           category strip, the guide, My gear). The band renders the recognition itself, below. */
        showClue={false}
      />
    )
    // The product page is where the note lives and where it can be edited, so the link is the
    // primary action; Done is the quiet way out (Guillaume, 2026-09-22). Without a link, Done is
    // the only action and keeps the emphasis.
    const doneAndSee = (
      <div className={styles.actions}>
        {product.href && (
          // A new tab: the reader keeps their place on the category grid (Guillaume, 2026-09-11).
          <a
            className={styles.primary}
            href={ownersDeepLink(product.href)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {SEE_ON_PAGE}
          </a>
        )}
        <button
          type="button"
          className={product.href ? styles.secondary : styles.primary}
          onClick={close}
        >
          {'Done'}
        </button>
      </div>
    )
    // The accept result (2026-10-07): the review's thread in the community leads; a signed-out
    // (unlinked) author is told what signing in adds, in DealHunt's figures only; a linked one is
    // offered a question for the other owners, seeded from their answers — never the sentence.
    const trailingSlash = publicConfig('useTrailingSlash')
    const linked = Boolean(claimed) || Boolean(me?.profile.claimed)
    const thread = communityThreadHref(product.href, result.note.id, trailingSlash)
    const seedQuestion = askOwnersSeed(product.name, answers.reads, questions?.reads ?? null)
    const panel = acceptPanel(linked, linkFailure?.retry)
    const askHref =
      panel === 'ask' ? askOwnersHref(product.href, seedQuestion, trailingSlash) : null
    const makeItCount = panel === 'make_it_count'
    return (
      <section className={styles.band} aria-live="polite">
        {outcome === 'accept' && (
          <>
            <h3 className={styles.title}>{'Published — thank you.'}</h3>
            {linkedCopy ? (
              <>
                <p className={styles.lead}>{linkedCopy.lead}</p>
                {linkedCopy.extra && <p className={styles.fine}>{linkedCopy.extra}</p>}
              </>
            ) : (
              <p className={styles.lead}>
                {`You're owner #${result.owners_now} of the ${
                  product.name
                }. Your owner review is live on the product page under @${
                  result.note.handle ?? 'your pseudonym'
                }.`}
              </p>
            )}
            {/* What this publish earned in the community (phase 1b): the founding number, the
                points, the tier. DealHunt's figures, on the uncached publish answer; after a
                sign-in on this result, the claim's. Signed out, the "Make it count" box says
                what signing in adds instead. */}
            {linkFailure && !linked && (
              <p className={styles.fine}>{ownerLinkMessage(linkFailure.lead)}</p>
            )}
            {makeItCount ? (
              <MakeItCount
                items={makeItCountItems(result.community)}
                trackOwner={trackOwner}
                signedInEmail={sessionEmail}
                retry={linkFailure?.retry === 'sign_in' ? 'sign_in' : 'link'}
                onClaimed={r => {
                  setClaimed(r)
                  setLinkFailure(null)
                  onClaimed?.(r)
                }}
              />
            ) : (
              // Only a linked author's standing: `none` is a signed-in reader whose link failed
              // for good (no verified email, `attemptLink`) — "Sign in to …" would be false and
              // no retry can succeed; `linkFailure.lead` above already says what to do.
              panel === 'ask' &&
              publishStandingLines(claimed?.community ?? result.community, linked).map(line => (
                <p key={line} className={styles.fine}>
                  {line}
                </p>
              ))
            )}
            {/* After a claim (at Publish or from Make it count): the way into the
                conversation — the review's thread, live on accept. */}
            {claimed && <PostClaimInvite thread={thread} />}
            {summary}
            {photosDropped && <p className={styles.fine}>{PHOTOS_COPY.dropped}</p>}
            {emailLine && <p className={styles.fine}>{emailLine}</p>}
            {product.href && <p className={styles.fine}>{RESULT_TIMING_LINE}</p>}
            <CoachingLines coaching={result.verdict.coaching} show="recognition" />
            <AcceptActions thread={thread} productHref={product.href} onClose={close} />
            {askHref && <AskOwners seed={seedQuestion} href={askHref} />}
          </>
        )}
        {outcome === 'review' && (
          <>
            <h3 className={styles.title}>
              {signInPublishes ? 'It passed our checks.' : readerMessage}
            </h3>
            {claimed ? (
              <>
                <p className={styles.lead}>
                  {liveHere
                    ? `Published — your review is live under @${claimed.handle}.`
                    : `Linked to your account as @${claimed.handle}.${
                        liveHere === false ? ` ${OWNER_HELD_MODERATOR_MESSAGE}` : ''
                      }`}
                </p>
                {renamedLine && <p className={styles.fine}>{renamedLine}</p>}
                {liveHere === false && heldEmailLine(claimed.email_masked) && (
                  <p className={styles.fine}>{heldEmailLine(claimed.email_masked)}</p>
                )}
                {/* The thread only once the claim published this review; a held one has no
                    public thread yet, so the community home. */}
                <PostClaimInvite thread={liveHere ? thread : null} />
              </>
            ) : (
              <p className={styles.lead}>
                {signInPublishes
                  ? heldResultLead(result.note.handle, result.note.status === 'published')
                  : `${OWN_PRIVATE_LABEL}.`}
              </p>
            )}
            {summary}
            {photosDropped && <p className={styles.fine}>{PHOTOS_COPY.dropped}</p>}
            {/* An anonymous author is praised at submit even though the note still waits for a
                moderator (#149): the recognition sits with the verdict, above the optional
                sign-in, never below it. DealHunt sends the PRESENT tense for a `review` peer line
                — it is rendered as sent, never re-worded into an ordinal. */}
            <CoachingLines coaching={result.verdict.coaching} show="recognition" />
            {doneAndSee}
            {/* The sign-in is an option offered after the reader has seen their note, not the
                price of seeing it. */}
            {!claimed && signInPublishes && (
              <ClaimCta
                trackOwner={trackOwner}
                location="band"
                // LINK_UNAVAILABLE_LEAD says the review published anonymously — true on accept
                // only; a held review keeps the held lead.
                lead={
                  linkFailure && linkFailure.lead !== LINK_UNAVAILABLE_LEAD
                    ? linkFailure.lead
                    : HELD_CLAIM_LEAD
                }
                signedInEmail={sessionEmail}
                retry={linkFailure?.retry ?? 'link'}
                onClaimed={r => {
                  setClaimed(r)
                  setLinkFailure(null)
                  onClaimed?.(r)
                }}
              />
            )}
          </>
        )}
        {outcome === 'reject' && (
          <>
            <h3 className={styles.title}>{'Not quite — one change and it can go up.'}</h3>
            {/* The check reads the sentence only (the answers are context): show the sentence
                beside what it needs, so the reader can see what to change. */}
            <figure className={styles.judged}>
              <figcaption className={styles.judgedLabel}>{'Your sentence'}</figcaption>
              <blockquote className={styles.judgedText}>{result.note.note}</blockquote>
            </figure>
            <p className={styles.lead}>
              <strong>{'What it needs: '}</strong>
              {readerMessage ?? 'Say what happened when you used it.'}
            </p>
            <p className={styles.fine}>{REJECT_SCOPE_LINE}</p>
            <div className={styles.actions}>
              {result.note.resubmits_left > 0 && (
                <button type="button" className={styles.primary} onClick={editAndResend}>
                  {'Edit and resend'}
                </button>
              )}
              {!appealed ? (
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => void appeal()}
                >
                  {'Ask for a human look'}
                </button>
              ) : (
                <span className={styles.fine}>
                  {'Sent to a moderator — you will see the outcome in My gear.'}
                </span>
              )}
              <button type="button" className={styles.ghost} onClick={close}>
                {'Close'}
              </button>
            </div>
            {error && <p className={styles.error}>{error}</p>}
          </>
        )}
        {outcome === 'hold' && (
          <>
            <h3 className={styles.title}>{'Saved — we could not check it just now.'}</h3>
            <p className={styles.lead}>
              {readerMessage ??
                'It will be checked again automatically. Only you can see it until then.'}
            </p>
            {summary}
            {photosDropped && <p className={styles.fine}>{PHOTOS_COPY.dropped}</p>}
            {doneAndSee}
          </>
        )}
      </section>
    )
  }

  // ── the form ───────────────────────────────────────────────────────────────
  // The seeded buy-again answer, said instead of asked (step 1's header line, and step 2 in the
  // control's place) until the reader taps "change".
  const buyAgainLine =
    !buyAgainAsked && typeof answers.buy_again === 'boolean' ? (
      <p className={styles.lead}>
        {`${buyAgainAck(answers.buy_again)} `}
        <button type="button" className={styles.link} onClick={() => setBuyAgainReopened(true)}>
          {BUY_AGAIN_CHANGE}
        </button>
      </p>
    ) : null
  return (
    <section className={styles.band} aria-label={`Owner review of the ${product.name}`}>
      <div className={styles.head}>
        <h3
          ref={headingRef}
          tabIndex={-1}
          className={clsx(styles.title, { [styles.srOnly]: hideTitle })}
        >
          {existing
            ? `Edit your review of the ${product.name}`
            : `Your owner review of the ${product.name}`}
        </h3>
        <button type="button" className={styles.close} aria-label="Close" onClick={close}>
          {'×'}
        </button>
      </div>
      {/* `roster` / `seated` are guide-only asks (part B reports the originating ask); `rail` is
          also the PDP's, so it gets no intro. */}
      {(location === 'guide' ||
        location === 'strip' ||
        location === 'roster' ||
        location === 'seated') &&
        !existing &&
        step === 1 && (
          // Opened by the reference strip's Yes / No (a guide, or a category page since
          // 2026-09-18): a tap far from any sentence that said what a note is — so the band makes
          // the case here, in one line.
          <p className={styles.intro}>
            <span className={styles.introHook}>{BAND_INTRO.hook}</span>
            {` ${BAND_INTRO.pitch} `}
            <a href={BAND_INTRO.link.href} target="_blank" rel="noopener noreferrer">
              {BAND_INTRO.link.label}
            </a>
          </p>
        )}
      <ol className={styles.steps} aria-label="Steps">
        {(['Ownership', 'Quick reads', 'Your sentence'] as const).map((label, i) => (
          <li
            key={label}
            className={clsx(styles.step, {
              [styles.stepActive]: step === i + 1,
              [styles.stepDone]: step > i + 1,
            })}
          >
            {`${i + 1}. ${label}`}
          </li>
        ))}
      </ol>

      {questionsError && (
        <p className={styles.error}>{"Owner reviews aren't set up for this category yet."}</p>
      )}
      {questionsFailed && (
        <p className={styles.error}>
          {'The questions did not load. '}
          <button
            type="button"
            className={styles.link}
            onClick={() => setQuestionsAttempt(n => n + 1)}
          >
            {'Try again'}
          </button>
        </p>
      )}
      {!questions && !questionsError && !questionsFailed && (
        <p className={styles.fine}>{'Loading…'}</p>
      )}

      {questions && step === 1 && (
        <div className={styles.fields}>
          {buyAgainLine}
          {/* The gate, said once before anything is written (signed out, unlinked, resolved);
              the community form says it in its own panel (hostPanels). */}
          {gateActive && !hostPanels && <p className={styles.gateStep1}>{SIGN_IN_GATE.step1}</p>}
          <fieldset className={styles.group}>
            <legend className={styles.legend}>{'How long have you had it?'}</legend>
            <div className={styles.chips}>
              {questions.ownership_length.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={clsx(styles.chip, {
                    [styles.chipOn]: answers.ownership_length === opt.id,
                  })}
                  aria-pressed={answers.ownership_length === opt.id}
                  onClick={() => setAnswers(a => ({ ...a, ownership_length: opt.id }))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset className={styles.group}>
            <legend className={styles.legend}>{'Still using it?'}</legend>
            <div className={styles.chips}>
              {questions.still_using.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={clsx(styles.chip, { [styles.chipOn]: answers.still_using === opt.id })}
                  aria-pressed={answers.still_using === opt.id}
                  onClick={() => setAnswers(a => ({ ...a, still_using: opt.id }))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset className={styles.group}>
            <legend className={styles.legend}>
              {'What do you mostly use it for? (optional, up to six)'}
            </legend>
            <div className={styles.chips}>
              {questions.use_cases.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={clsx(styles.chip, {
                    [styles.chipOn]: answers.use_cases.includes(opt.id),
                  })}
                  aria-pressed={answers.use_cases.includes(opt.id)}
                  onClick={() => toggleUseCase(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </fieldset>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              disabled={!step1Ok}
              onClick={() => goTo(2)}
            >
              {'Next'}
            </button>
          </div>
        </div>
      )}

      {questions && step === 2 && (
        <div className={styles.fields}>
          <p className={styles.fine}>{'Compared with what you expected when you bought it:'}</p>
          {questions.reads.map(read => (
            <fieldset key={read.id} className={styles.group}>
              <legend className={styles.legend}>{read.label}</legend>
              <div className={styles.segments} role="group" aria-label={read.label}>
                {OWNER_READ_VALUES.map(value => (
                  <button
                    key={value}
                    type="button"
                    className={clsx(styles.segment, styles[`segment_${value}`], {
                      [styles.segmentOn]: answers.reads[read.id] === value,
                    })}
                    aria-pressed={answers.reads[read.id] === value}
                    onClick={() => setRead(read.id, value)}
                  >
                    {READ_LABEL[value]}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
          {anyWorse && (
            <fieldset className={styles.group}>
              <legend className={styles.legend}>{'What gave out first?'}</legend>
              <div className={styles.chips}>
                {questions.gave_out.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={clsx(styles.chip, { [styles.chipOn]: answers.gave_out === opt.id })}
                    aria-pressed={answers.gave_out === opt.id}
                    onClick={() =>
                      setAnswers(a => ({ ...a, gave_out: a.gave_out === opt.id ? null : opt.id }))
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          {questions.hours_per_charge.enabled && (
            <div className={styles.group}>
              <label className={styles.legend} htmlFor="owner-hours">
                {'Hours you actually get per charge (optional)'}
              </label>
              <input
                id="owner-hours"
                type="number"
                inputMode="decimal"
                className={styles.number}
                min={questions.hours_per_charge.min}
                max={questions.hours_per_charge.max}
                step={0.5}
                value={answers.hours_per_charge ?? ''}
                onChange={e =>
                  setAnswers(a => ({
                    ...a,
                    hours_per_charge: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              />
            </div>
          )}
          {buyAgainAsked ? (
            <fieldset className={styles.group}>
              <legend className={styles.legend}>{'Would you buy it again?'}</legend>
              <div className={styles.chips}>
                {[
                  { v: true, label: 'Yes' },
                  { v: false, label: 'No' },
                ].map(opt => (
                  <button
                    key={String(opt.v)}
                    type="button"
                    className={clsx(styles.chip, { [styles.chipOn]: answers.buy_again === opt.v })}
                    aria-pressed={answers.buy_again === opt.v}
                    onClick={() => setAnswers(a => ({ ...a, buy_again: opt.v }))}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </fieldset>
          ) : (
            buyAgainLine
          )}
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={() => goTo(1)}>
              {'Back'}
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={!step2Ok}
              onClick={() => goTo(3)}
            >
              {'Next'}
            </button>
          </div>
        </div>
      )}

      {questions && step === 3 && (
        <div className={styles.fields}>
          <div className={styles.group}>
            <label className={styles.legend} htmlFor="owner-note">
              {'What nobody tells you before buying'}
            </label>
            {revisionHint && (
              <p className={styles.revisionHint}>
                <strong>{'What it needs: '}</strong>
                {revisionHint}
              </p>
            )}
            <textarea
              ref={noteFieldRef}
              id="owner-note"
              className={styles.textarea}
              rows={3}
              maxLength={OWNER_NOTE_MAX_CHARS + 20}
              placeholder="One or two sentences from living with it — comfort, battery, what broke, what surprised you."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
            <div
              className={clsx(styles.counter, {
                [styles.counterBad]: noteLength > OWNER_NOTE_MAX_CHARS,
              })}
            >
              {noteLength < OWNER_NOTE_MIN_CHARS
                ? `${OWNER_NOTE_MIN_CHARS - noteLength} more characters`
                : `${noteLength} / ${OWNER_NOTE_MAX_CHARS}`}
            </div>
          </div>
          {/* Right under the sentence (2026-10-07): on a phone the photos and the preview pushed
              the one sign-in below the fold. Opens the dialog only (#81). */}
          {showSignInFirst && <PrePublishSignIn onSignIn={signInFirst} />}
          {/* With hostPanels the composer prints its own preview, so the gate keeps its box here;
              otherwise it is the footer of the band's preview card below. */}
          {gateShows && hostPanels && gatePanel(false)}
          <ReviewPhotosBlock
            state={photoSet}
            signedIn={ownerAuth.signedIn}
            // An untouched edit sends no `media_ids` (the photos stay as they are): nothing to warn.
            // Gated: the send goes out only linked (or stops), so the tiles never say "left out".
            canAttach={canAttachPhotos || gatedCreate || (Boolean(existing) && !photoSet.touched)}
            onSignIn={ownerAuth.showLogIn}
            // One sign-in button in the form: the pre-publish line above carries it, or (gated)
            // the primary "Sign in to publish".
            signInButton={!showSignInFirst && !gateShows}
            gated={gatedCreate}
          />
          {!hostPanels && (
            <ReviewPreview
              draft={draft}
              questions={questions}
              productName={product.name}
              handle={me?.profile.handle ?? null}
              tier={me?.community?.member ? me.community.tier : null}
              footer={gateShows ? gatePanel(true) : null}
              photos={previewPhotos}
            />
          )}
          {/* Honeypot (§5): hidden from people, filled by scripts. Never a visible control. */}
          <div className={styles.trap} aria-hidden="true">
            <label htmlFor="owner-website">{'Website'}</label>
            <input
              ref={websiteRef}
              id="owner-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              defaultValue=""
            />
          </div>
          <div ref={turnstile.containerRef} className={styles.turnstile} />
          <p className={styles.fine}>
            {`${STEP3_FINE} `}
            <a
              href="/how-we-verify-owners/"
              className={styles.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {'How we verify owners'}
            </a>
          </p>
          {identity && (
            <p className={styles.identity}>
              {identity.text}
              {identity.canToggle && (
                <button
                  type="button"
                  className={styles.identityToggle}
                  onClick={() => setLinkAccount(v => !v)}
                >
                  {linkAccount ? "Don't link" : 'Link my account'}
                </button>
              )}
              {identity.extra && <span className={styles.identityExtra}>{identity.extra}</span>}
            </p>
          )}
          {conflict && (
            <div className={styles.conflict} role="alert">
              {me?.profile.email_masked ? `${conflict} (${me.profile.email_masked})` : conflict}
              {existing
                ? ' Sign out and back in with that account to edit this review.'
                : ' Publishing now would file the review under that account.'}
              <div className={styles.conflictActions}>
                {!existing && (
                  <button
                    type="button"
                    className={styles.primary}
                    disabled={busy}
                    onClick={() => void startFresh()}
                  >
                    {busy ? 'Starting fresh…' : 'Start fresh on this browser'}
                  </button>
                )}
                <button type="button" className={styles.ghost} onClick={() => setConflict(null)}>
                  {'Cancel'}
                </button>
              </div>
            </div>
          )}
          {editCapped ? (
            /* #150: the ceiling on edits to a note that is not live yet. Information, not a
               refusal — so no red, no "try again", and no Publish: another send cannot pass.
               Back goes too; it only leads to the same wall. */
            <>
              <p className={styles.notice} role="status">
                {editCapped}
              </p>
              <div className={styles.actions}>
                <button type="button" className={styles.primary} onClick={close}>
                  {'Done'}
                </button>
              </div>
            </>
          ) : (
            <>
              {error && <p className={styles.error}>{error}</p>}
              {gateStop?.retry === 'link' && !gateShows && (
                <p className={styles.error} role="alert">
                  {gateStop.text}
                </p>
              )}
              <div className={styles.actions}>
                <button type="button" className={styles.ghost} onClick={() => goTo(2)}>
                  {'Back'}
                </button>
                {gateShows ? (
                  // The gate: this click only opens the sign-in dialog (through `publish`, which
                  // guards the same way) — nothing is claimed or sent.
                  <button
                    type="button"
                    className={styles.primary}
                    // A second click while the dialog is open would fire claim_started{gate} twice.
                    disabled={busy || ownerAuth.loginOpen}
                    onClick={() => void publish()}
                  >
                    {SIGN_IN_GATE.button}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={clsx(styles.primary, { [styles.working]: phase !== null })}
                    disabled={
                      !step3Ok || busy || conflict !== null || uploadingPhotos || gatePending
                    }
                    aria-busy={phase !== null}
                    onClick={() => void publish()}
                  >
                    {phase !== null && <span className={styles.spinner} aria-hidden="true" />}
                    {publishLabel}
                  </button>
                )}
              </div>
              {/* The long check (2026-09-20): once the wait passes the still-checking bar it is
                  likely holding two model calls, so offer the way out rather than ask the
                  reader to keep staring at the button. Tied to the same flag as the label, so
                  a fast verdict never shows it. */}
              {/* Under the gate, picked tiles wait for the session: not "still uploading". */}
              {uploadingPhotos && phase === null && !gateShows && (
                <p className={styles.fine}>{PHOTOS_COPY.uploading}</p>
              )}
              {phase === 'check' && stillChecking && (
                <p className={styles.fine}>{LONG_CHECK_ESCAPE_LINE}</p>
              )}
              <p className={styles.srOnly} role="status">
                {phase !== null ? publishLabel : ''}
              </p>
            </>
          )}
        </div>
      )}
    </section>
  )
}

export default OwnerNoteBand
